// Tiny reactive core (~no deps). Covers the derived-state patterns Svelte runes
// give us: signal() for state, computed() for derived values, effect() for
// reactions (including re-render). Dependency tracking is dynamic: whatever
// signal is read during an effect/computed run becomes a dependency — and is
// re-tracked from scratch on every run, so stale dependencies drop away.
//
// Lifecycle: effects are owned. One created during another effect's run is a
// CHILD of that effect and is disposed when the parent re-runs or is disposed
// (so bindings inside a mount() re-render don't accumulate). A top-level effect
// registers with the current route scope; the router rotates scopes on
// navigation (nextScope), disposing the previous route's effects. Async work
// that finishes after a navigation registers into the scope current at that
// moment, so its effects are reclaimed one navigation later — bounded, not a
// leak.

type Runnable = () => void;

interface EffectNode {
	run: Runnable;
	deps: Set<Set<EffectNode>>; // every subscriber-set this node appears in
	children: Array<() => void>;
	dead: boolean;
}

let activeEffect: EffectNode | null = null;

// Top-level effects collect here until the router rotates the scope.
let collector: Array<() => void> = [];

/** Rotate the top-level effect scope (the router calls this per navigation).
 * Returns a disposer for everything collected since the previous rotation. */
export function nextScope(): () => void {
	const prev = collector;
	collector = [];
	return () => {
		for (const d of prev) d();
		prev.length = 0;
	};
}

export interface Signal<T> {
	(): T;
	set(value: T): void;
	update(fn: (prev: T) => T): void;
}

export function signal<T>(initial: T): Signal<T> {
	let value = initial;
	const subs = new Set<EffectNode>();

	const read = (() => {
		if (activeEffect && !activeEffect.dead) {
			subs.add(activeEffect);
			activeEffect.deps.add(subs);
		}
		return value;
	}) as Signal<T>;

	read.set = (next: T) => {
		if (Object.is(next, value)) return;
		value = next;
		// Snapshot: subscribers may re-subscribe — or dispose each other — as
		// they run (a parent re-running disposes its children mid-iteration).
		for (const sub of [...subs]) {
			if (!sub.dead) sub.run();
		}
	};
	read.update = (fn) => read.set(fn(value));

	return read;
}

/** Run fn now and again whenever a signal it read changes. Returns a disposer;
 * most callers ignore it and rely on ownership (parent effect / route scope). */
export function effect(fn: Runnable): () => void {
	const node: EffectNode = { run: () => undefined, deps: new Set(), children: [], dead: false };

	const untrack = () => {
		for (const set of node.deps) set.delete(node);
		node.deps.clear();
	};
	const disposeChildren = () => {
		for (const d of node.children) d();
		node.children.length = 0;
	};

	node.run = () => {
		if (node.dead) return;
		// Fresh slate each run: re-track dependencies, recreate children.
		untrack();
		disposeChildren();
		const prev = activeEffect;
		activeEffect = node;
		try {
			fn();
		} finally {
			activeEffect = prev;
		}
	};

	const dispose = () => {
		if (node.dead) return;
		node.dead = true;
		untrack();
		disposeChildren();
	};

	// Ownership: nested effects die with their parent's next run/disposal;
	// top-level effects belong to the current route scope.
	if (activeEffect && !activeEffect.dead) activeEffect.children.push(dispose);
	else collector.push(dispose);

	node.run();
	return dispose;
}

export function computed<T>(fn: () => T): () => T {
	const s = signal<T>(undefined as unknown as T);
	effect(() => s.set(fn()));
	return () => s();
}
